export type LibraryShotsNode =
  | { level: "episode"; episodeId: number }
  | { level: "animatic"; episodeId: number }
  | { level: "scene"; sceneId: number }
  | { level: "shot"; shotId: number }

/** Секции медиа сущностей: папка у всех одна, различается только вид записи. */
export const LIBRARY_ENTITY_SECTIONS = ["characters", "locations", "props", "scenes_3d"] as const
export type LibraryEntitySection = (typeof LIBRARY_ENTITY_SECTIONS)[number]

export function isLibraryEntitySection(value: string | null): value is LibraryEntitySection {
  return value != null && (LIBRARY_ENTITY_SECTIONS as readonly string[]).includes(value)
}

/** Выбранная папка Библиотеки (deep-linkable в query страницы). */
export type LibrarySelection =
  | { section: "shots"; node: LibraryShotsNode | null }
  | { section: "canvas"; canvasId: number | null }
  | { section: "flow"; flowId: number | null; orphans?: boolean }
  | { section: "sketches" }
  | { section: "uploads" }
  | { section: LibraryEntitySection; entityId: number | null }

function positiveId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const id = Number(raw)
  return id > 0 ? id : null
}

/** null — в query нет валидной секции (страница подставляет дефолт). */
export function parseLibraryUrl(search: string): LibrarySelection | null {
  const params = new URLSearchParams(search)
  const section = params.get("section")
  if (section === "canvas") {
    return { section: "canvas", canvasId: positiveId(params.get("canvas_id")) }
  }
  if (section === "flow") {
    if (params.get("orphans") === "1") return { section: "flow", flowId: null, orphans: true }
    return { section: "flow", flowId: positiveId(params.get("flow_id")) }
  }
  if (section === "sketches") {
    return { section: "sketches" }
  }
  if (section === "uploads") {
    return { section: "uploads" }
  }
  // Legacy previzes tree → 3D Scenes entity section.
  if (section === "previzes") {
    return { section: "scenes_3d", entityId: null }
  }
  if (isLibraryEntitySection(section)) {
    return { section, entityId: positiveId(params.get("entity_id")) }
  }
  if (section !== "shots") return null
  // Уровни выбора взаимоисключающие — самый глубокий побеждает.
  const shotId = positiveId(params.get("shot_id"))
  if (shotId != null) return { section: "shots", node: { level: "shot", shotId } }
  const sceneId = positiveId(params.get("scene_id"))
  if (sceneId != null) return { section: "shots", node: { level: "scene", sceneId } }
  const animaticEpisodeId = positiveId(params.get("animatic_episode_id"))
  if (animaticEpisodeId != null) return { section: "shots", node: { level: "animatic", episodeId: animaticEpisodeId } }
  const episodeId = positiveId(params.get("episode_id"))
  if (episodeId != null) return { section: "shots", node: { level: "episode", episodeId } }
  return { section: "shots", node: null }
}

export function buildLibraryQuery(selection: LibrarySelection): string {
  const params = new URLSearchParams({ section: selection.section })
  if (selection.section === "shots") {
    const node = selection.node
    if (node?.level === "shot") params.set("shot_id", String(node.shotId))
    else if (node?.level === "scene") params.set("scene_id", String(node.sceneId))
    else if (node?.level === "animatic") params.set("animatic_episode_id", String(node.episodeId))
    else if (node?.level === "episode") params.set("episode_id", String(node.episodeId))
  } else if (selection.section === "canvas") {
    if (selection.canvasId != null) params.set("canvas_id", String(selection.canvasId))
  } else if (selection.section === "flow") {
    if (selection.orphans) params.set("orphans", "1")
    else if (selection.flowId != null) params.set("flow_id", String(selection.flowId))
  } else if (selection.section !== "sketches" && selection.section !== "uploads" && selection.entityId != null) {
    params.set("entity_id", String(selection.entityId))
  }
  return params.toString()
}

/** Внутристраничная смена папки: replaceState с сохранением window.history.state (конвенция canvas-generation-url). */
export function syncLibraryUrl(selection: LibrarySelection): void {
  if (typeof window === "undefined") return
  const next = `${window.location.pathname}?${buildLibraryQuery(selection)}${window.location.hash}`
  window.history.replaceState(window.history.state, "", next)
}
